import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { MapContainer, TileLayer, Marker, useMap } from "react-leaflet";
import L from "leaflet";
import { toast } from "sonner";
import { api } from "@/App";
import "leaflet/dist/leaflet.css";

// Fix Leaflet default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// Custom avatar marker
const createAvatarIcon = (user) => {
  const isVerified = user.is_verified;
  const ringColor = isVerified ? '#D4A574' : 'transparent';
  const ringWidth = isVerified ? '2px' : '0';
  
  return L.divIcon({
    className: 'custom-avatar-marker',
    html: `
      <div style="
        width: 44px;
        height: 44px;
        border-radius: 50%;
        border: ${ringWidth} solid ${ringColor};
        padding: 2px;
        background: white;
        box-shadow: 0 2px 8px rgba(0,0,0,0.1);
      ">
        <div style="
          width: 100%;
          height: 100%;
          border-radius: 50%;
          background-image: url('${user.picture || ''}');
          background-size: cover;
          background-position: center;
          background-color: #E5E7EB;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          color: #6B7280;
        ">
          ${!user.picture ? user.name?.charAt(0)?.toUpperCase() || '?' : ''}
        </div>
      </div>
    `,
    iconSize: [44, 44],
    iconAnchor: [22, 22],
  });
};

// User position marker (simple neutral dot)
const userPositionIcon = L.divIcon({
  className: 'user-position-marker',
  html: `<div style="
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: #9CA3AF;
    border: 2px solid white;
    box-shadow: 0 1px 4px rgba(0,0,0,0.2);
  "></div>`,
  iconSize: [12, 12],
  iconAnchor: [6, 6],
});

// Map center controller
const MapController = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView(center, 17);
    }
  }, [center, map]);
  return null;
};

export default function Nearby() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [nearbyUsers, setNearbyUsers] = useState([]);
  const [myLocation, setMyLocation] = useState(null);
  
  const fetchNearby = async () => {
    try {
      const response = await api.get('/nearby');
      setNearbyUsers(response.data.nearby_users);
      setMyLocation(response.data.my_location);
    } catch (error) {
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchNearby();
    const interval = setInterval(fetchNearby, 60000);
    return () => clearInterval(interval);
  }, []);
  
  // Generate pseudo-random but consistent positions for users on map
  const getUserPosition = (user, baseLocation) => {
    if (!baseLocation) return null;
    const hash = user.user_id.split('').reduce((a, c) => a + c.charCodeAt(0), 0);
    const angle = (hash % 360) * (Math.PI / 180);
    const distance = 0.0001 + (hash % 100) * 0.000003; // Small offset in degrees
    
    return [
      baseLocation.latitude + distance * Math.cos(angle),
      baseLocation.longitude + distance * Math.sin(angle)
    ];
  };
  
  if (loading) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center bg-white">
        <div className="animate-pulse text-gray-300">...</div>
      </div>
    );
  }
  
  const defaultCenter = myLocation 
    ? [myLocation.latitude, myLocation.longitude] 
    : [41.9109, 12.4764]; // Rome default
  
  return (
    <div className="page-content relative h-[calc(100vh-64px)]" data-testid="nearby-page">
      {/* Header - Minimal */}
      <div className="absolute top-0 left-0 right-0 z-[1000] px-4 pt-4">
        <div>
          <h1 className="text-xl font-medium text-gray-900">Qui</h1>
          <p className="text-sm text-gray-400">Ora</p>
        </div>
      </div>
      
      {/* Map */}
      <div className="absolute inset-0">
        <MapContainer
          center={defaultCenter}
          zoom={17}
          style={{ height: '100%', width: '100%' }}
          zoomControl={false}
          attributionControl={false}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MapController center={defaultCenter} />
          
          {/* User position */}
          {myLocation && (
            <Marker 
              position={[myLocation.latitude, myLocation.longitude]}
              icon={userPositionIcon}
            />
          )}
          
          {/* Nearby users */}
          {nearbyUsers.map((user) => {
            const position = getUserPosition(user, myLocation);
            if (!position) return null;
            
            return (
              <Marker
                key={user.user_id}
                position={position}
                icon={createAvatarIcon(user)}
                eventHandlers={{
                  click: () => navigate(`/app/user/${user.user_id}`)
                }}
              />
            );
          })}
        </MapContainer>
      </div>
    </div>
  );
}
